import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Divider, CircularProgress,
  Switch, FormControlLabel,
} from '@mui/material';
import { styleForDegree } from '../utils/ElliottDegreeStyles';

/**
 * The Elliott wave summary panel.
 *
 * Presentational only — the analysis arrives already done. Keeping the fetch in App means this
 * renders the same whether the result came from a click, a replay, or a test.
 */

/** Bias colours. Gray for NEUTRAL rather than a washed-out green: it is a real answer, not a weak one. */
const BIAS_STYLES = {
  BULLISH: { bg: '#1b5e20', label: 'BULLISH' },
  BEARISH: { bg: '#b71c1c', label: 'BEARISH' },
  NEUTRAL: { bg: '#546e7a', label: 'NEUTRAL' },
};

/**
 * Prices are formatted, never rounded away.
 *
 * A target and an invalidation are levels a trader acts on, so two decimals throughout — and a
 * missing one shows as a dash rather than as 0.00, which would read as a real price of zero.
 */
function price(value) {
  return (typeof value === 'number' && Number.isFinite(value)) ? value.toFixed(2) : '—';
}

function MetricCard({ label, value, hint, accent }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 180, p: 1.5, borderRadius: 1,
      border: '1px solid #e0e0e0', bgcolor: '#fafafa',
    }}>
      <Typography sx={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, fontFamily: 'monospace', color: accent || '#212121' }}>
        {value}
      </Typography>
      {hint && <Typography sx={{ fontSize: 11, color: '#888' }}>{hint}</Typography>}
    </Box>
  );
}

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
  accent: PropTypes.string,
};

/**
 * The wave label out of a position string like "Wave B of 3 (Zigzag, MICRO)".
 *
 * Read from the text rather than sent as its own field, because the engine already composes the
 * position for display and a second source for the same label is a second thing to keep in step.
 */
function legLabel(result) {
  const match = /^Wave\s+(\S+)\s+of/.exec(result?.currentPosition || '');
  return match ? `Wave ${match[1]} retracement` : 'Counter-trend leg';
}

/**
 * Whether the leg stop is worth its own card.
 *
 * Only when it differs from the count level, which happens exactly on a counter-trend leg — wave 3
 * begins where wave 2 ended, so for a with-trend leg the two are the same number and two cards
 * would imply a distinction that is not there.
 */
function showsSeparateLegStop(result) {
  return typeof result?.legInvalidationPrice === 'number'
    && Number.isFinite(result.legInvalidationPrice)
    && result.legInvalidationPrice !== result.invalidationPrice;
}

/** True when the primary count carries enough pivots to draw a polyline through. */
export function canDrawOverlay(result) {
  const points = result?.primaryConclusion?.waveOverlay?.points;
  return Array.isArray(points) && points.length >= 2;
}

export default function ElliottWaveDialog({
  open, onClose, loading, error, result, overlayShown, onToggleOverlay,
}) {
  const bias = BIAS_STYLES[result?.directionalBias] || BIAS_STYLES.NEUTRAL;
  const legBias = BIAS_STYLES[result?.activeLegBias] || BIAS_STYLES.NEUTRAL;
  const primary = result?.primaryConclusion;
  const counts = Array.isArray(result?.top3WaveCounts) ? result.top3WaveCounts : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
          Elliott Wave Analysis
        </Typography>
        {result && (
          <Typography component="span" sx={{ color: '#666', fontSize: '0.95rem' }}>
            {result.symbol} · {result.timeframe} · {result.barCount} bars
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
            <CircularProgress size={24} />
            <Typography>Labelling the series…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#fdecea', border: '1px solid #f5c6cb' }}>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Analysis failed</Typography>
            <Typography sx={{ fontSize: 14 }}>{error}</Typography>
          </Box>
        )}

        {/*
          A series too short or too flat to label is a RESULT, not a failure, and is shown as one.
          Rendering it in the error box would tell the customer something went wrong when the
          honest answer is that this chart has nothing to label.
        */}
        {!loading && !error && result && !result.hasConclusion && (
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No valid wave count</Typography>
            <Typography sx={{ fontSize: 14, color: '#555' }}>
              {result.message || 'Nothing in this series satisfied the strict rules of any pattern.'}
            </Typography>
          </Box>
        )}

        {!loading && !error && result && result.hasConclusion && (
          <>
            {/*
              Macro and leg are shown as two chips whenever they disagree.

              A single BEARISH badge above a target sitting over the market is what was reported as
              a contradiction, and it was not one — the structure falls while wave B rises. Both
              facts are true and the badge could only ever carry one of them, so it now carries
              both and says which is which.
            */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={result.counterTrendLeg ? `${bias.label} (Macro)` : bias.label}
                sx={{
                  bgcolor: bias.bg, color: '#fff', fontWeight: 700, fontSize: 16,
                  height: 40, px: 1.5, borderRadius: 1,
                }}
              />
              {result.counterTrendLeg && (
                <>
                  <Typography sx={{ color: '#999', fontSize: 20 }}>|</Typography>
                  <Chip
                    label={`${legLabel(result)} (${legBias.label.toLowerCase()} leg)`}
                    variant="outlined"
                    sx={{
                      borderColor: legBias.bg, color: legBias.bg, fontWeight: 700,
                      fontSize: 15, height: 40, px: 1, borderRadius: 1, borderWidth: 2,
                    }}
                  />
                </>
              )}
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 17 }}>
                  {result.currentPosition}
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#666' }}>
                  Confidence {primary?.confidencePercent}% · analysed in {result.evaluationTimeMs} ms
                </Typography>
              </Box>
            </Box>

            {result.counterTrendLeg && (
              <Box sx={{
                p: 1.25, mb: 2, borderRadius: 1, bgcolor: '#fff8e1',
                border: '1px solid #ffe082', fontSize: 13,
              }}>
                The structure points <b>{bias.label.toLowerCase()}</b>, but the leg in progress runs
                against it. The target below belongs to that leg, not to the structure — it resumes{' '}
                {result.directionalBias === 'BULLISH' ? 'higher' : 'lower'} once the leg completes.
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <MetricCard
                label="Primary target"
                value={price(result.primaryTargetPrice)}
                hint={result.counterTrendLeg
                  ? `Projection for the ${legBias.label.toLowerCase()} leg in progress`
                  : 'Fibonacci projection for the wave in progress'}
                accent="#1b5e20"
              />
              {/*
                Two levels, because they answer different questions and only coincide on a
                with-trend leg. The leg stop is the one on the tradable side of the market during a
                retracement; the count level sits on the other side, correct for the pattern and
                useless as a stop for the wave actually being traded.
              */}
              {showsSeparateLegStop(result) && (
                <MetricCard
                  label="Invalidation (leg)"
                  value={price(result.legInvalidationPrice)}
                  hint="The leg in progress fails beyond this level"
                  accent="#e65100"
                />
              )}
              <MetricCard
                label={showsSeparateLegStop(result) ? 'Invalidation (count)' : 'Invalidation'}
                value={price(result.invalidationPrice)}
                hint="The whole count is wrong beyond this level"
                accent="#b71c1c"
              />
            </Box>

            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Recommendation</Typography>
            <Box sx={{
              p: 1.5, mb: 2, borderRadius: 1, bgcolor: '#f8f9fa',
              border: '1px solid #e0e0e0', fontSize: 14, lineHeight: 1.6,
            }}>
              {result.humanReadableRecommendation}
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              Alternate counts
            </Typography>
            {/*
              The primary is row 1 rather than being omitted. A trader comparing alternates needs
              the thing they are alternates TO on the same scale, in the same table.
            */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Pattern</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Degree</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Dir</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Confidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {counts.map((count, index) => (
                  <TableRow key={`${count.pattern}-${count.position}-${index}`}
                    sx={index === 0 ? { bgcolor: '#e8f0fe' } : undefined}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{count.pattern}</TableCell>
                    <TableCell>{count.degree}</TableCell>
                    <TableCell>{count.position}</TableCell>
                    <TableCell>{count.directionUp ? 'Up' : 'Down'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {count.confidencePercent}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {primary?.supportingGuidelines?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Supporting guidelines</Typography>
                <Box component="ul" sx={{ m: 0, pl: 3, fontSize: 13, color: '#555' }}>
                  {primary.supportingGuidelines.map(g => <li key={g}>{g}</li>)}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        {/*
          Enabled only when there is a count to draw. A switch that can be flipped while there are
          no pivots would report "on" and put nothing on the chart, which reads as a broken toggle
          rather than as an empty analysis.
        */}
        <FormControlLabel
          control={
            <Switch
              checked={!!overlayShown}
              disabled={!canDrawOverlay(result)}
              onChange={e => {
                if (typeof onToggleOverlay === 'function') onToggleOverlay(e.target.checked);
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: 14 }}>
              Show wave overlay on chart
              {result?.primaryConclusion?.degree && canDrawOverlay(result) && (
                <Typography component="span" sx={{ fontSize: 12, color: '#777', ml: 0.75 }}>
                  ({styleForDegree(result.primaryConclusion.degree).name})
                </Typography>
              )}
            </Typography>
          }
        />
        <Button onClick={onClose} variant="contained">CLOSE</Button>
      </DialogActions>
    </Dialog>
  );
}

ElliottWaveDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  result: PropTypes.shape({
    symbol: PropTypes.string,
    timeframe: PropTypes.string,
    barCount: PropTypes.number,
    evaluationTimeMs: PropTypes.number,
    hasConclusion: PropTypes.bool,
    message: PropTypes.string,
    directionalBias: PropTypes.string,
    currentPosition: PropTypes.string,
    primaryTargetPrice: PropTypes.number,
    invalidationPrice: PropTypes.number,
    // The leg's own direction and stop. They differ from the macro pair only on a counter-trend
    // leg, which is precisely the case that read as a contradiction.
    activeLegBias: PropTypes.string,
    counterTrendLeg: PropTypes.bool,
    legInvalidationPrice: PropTypes.number,
    humanReadableRecommendation: PropTypes.string,
    primaryConclusion: PropTypes.object,
    top3WaveCounts: PropTypes.array,
  }),
  /** Whether the wave polyline is currently drawn on the price panel. */
  overlayShown: PropTypes.bool,
  /** Called with the requested state when the switch is flipped. */
  onToggleOverlay: PropTypes.func,
};
